import React, { useState, useEffect, useCallback } from 'react';
import { Ticket, Search, RefreshCw, ChevronLeft, ChevronRight, Send, ArrowLeft, ShieldCheck, Clock, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


const STATUS_STYLES = {
  open: { bg: 'bg-primary/10', text: 'text-primary', label: 'Open' },
  in_progress: { bg: 'bg-yellow-400/10', text: 'text-yellow-400', label: 'In Progress' },
  responded: { bg: 'bg-blue-400/10', text: 'text-blue-400', label: 'Responded' },
  closed: { bg: 'bg-muted-foreground/10', text: 'text-muted-foreground', label: 'Closed' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function TicketConversation({ ticket, onBack, onRespond, onUnban }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [unbanning, setUnbanning] = useState(false);
  const messages = ticket.messages || [];
  const isBanAppeal = ticket.category === 'ban_appeal';
  const st = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    await onRespond(reply.trim());
    setReply('');
    setSending(false);
  };

  const handleUnban = async () => {
    setUnbanning(true);
    // Get user_id from the ticket
    const userId = ticket.user_id;
    try {
      const res = await authFetch(`${API}/api/admin/users/${userId}/unban`, {
        method: 'POST',
              });
      if (res.ok) {
        onUnban();
      }
    } catch {} finally {
      setUnbanning(false);
    }
  };

  return (
    <div data-testid="ticket-conversation" className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <button
          data-testid="ticket-back-btn"
          onClick={onBack}
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">{ticket.subject}</p>
            <span className={`text-[8px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${st.bg} ${st.text}`}>{st.label}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[9px] text-muted-foreground/50 font-mono">{ticket.ticket_id}</span>
            {ticket.user_name && (
              <span className="text-[9px] text-muted-foreground/50">{ticket.user_name} ({ticket.user_email})</span>
            )}
          </div>
        </div>
      </div>

      {/* Ban appeal info + Unban button */}
      {isBanAppeal && (
        <div className="bg-amber-500/[0.06] border border-amber-500/15 rounded-xl p-3 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[10px] font-medium text-amber-300">Ban Appeal</span>
            </div>
            {ticket.user_is_banned && (
              <button
                data-testid="unban-from-appeal"
                onClick={handleUnban}
                disabled={unbanning}
                className="text-[10px] px-3 py-1 rounded-lg bg-green-500/15 text-green-400 border border-green-500/20 hover:bg-green-500/25 transition-colors disabled:opacity-50 inline-flex items-center gap-1"
              >
                <ShieldCheck className="w-3 h-3" />
                {unbanning ? 'Unbanning...' : 'Unban User'}
              </button>
            )}
          </div>
          {ticket.ban_reason && (
            <p className="text-[10px] text-amber-400/60 mt-1.5">Original ban reason: {ticket.ban_reason}</p>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-3 min-h-0" style={{ maxHeight: '320px' }}>
        {messages.map((msg, i) => {
          const isAdmin = msg.from === 'admin';
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                  isAdmin
                    ? 'bg-primary/15 border border-primary/20'
                    : 'bg-white/[0.04] border border-white/5'
                }`}
                data-testid={`message-${i}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] font-medium ${isAdmin ? 'text-primary' : 'text-muted-foreground'}`}>
                    {msg.name || (isAdmin ? 'Team Swell' : 'User')}
                  </span>
                  <span className="text-[8px] text-muted-foreground/40">{timeAgo(msg.created_at)}</span>
                </div>
                <p className="text-xs leading-relaxed text-foreground/80">{msg.message}</p>
              </div>
            </motion.div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-[10px] text-muted-foreground/40">No messages yet</p>
          </div>
        )}
      </div>

      {/* Reply input */}
      {ticket.status !== 'closed' && (
        <div className="flex gap-2">
          <input
            data-testid="admin-reply-input"
            type="text"
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Reply as Team Swell..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
          />
          <button
            data-testid="admin-send-btn"
            onClick={handleSend}
            disabled={sending || !reply.trim()}
            className="px-3 py-2.5 rounded-xl bg-primary/20 text-primary border border-primary/30 hover:bg-primary/30 transition-colors disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [skip, setSkip] = useState(0);
  const [activeTicket, setActiveTicket] = useState(null);
  const limit = 20;

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      if (status) params.set('status', status);
      const res = await authFetch(`${API}/api/admin/tickets?${params}`);
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
        setTotal(data.total || 0);
      }
    } catch {} finally { setLoading(false); }
  }, [skip, search, category, status]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = async (ticketId) => {
    try {
      const res = await authFetch(`${API}/api/admin/tickets/${ticketId}`);
      if (res.ok) {
        const data = await res.json();
        setActiveTicket(data);
      }
    } catch {}
  };

  const handleRespond = async (message) => {
    if (!activeTicket) return;
    try {
      const res = await authFetch(`${API}/api/admin/tickets/${activeTicket.ticket_id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message }),
      });
      if (res.ok) {
        // Refresh the ticket
        await openTicket(activeTicket.ticket_id);
        await fetchTickets();
      }
    } catch {}
  };

  const handleUnban = () => {
    // Refresh after unban
    if (activeTicket) openTicket(activeTicket.ticket_id);
    fetchTickets();
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  // Show conversation view
  if (activeTicket) {
    return (
      <TicketConversation
        ticket={activeTicket}
        onBack={() => { setActiveTicket(null); fetchTickets(); }}
        onRespond={handleRespond}
        onUnban={handleUnban}
      />
    );
  }

  return (
    <div data-testid="admin-tickets-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Ticket className="w-4 h-4 text-primary/60" />
          <p className="text-xs text-muted-foreground">{total} total ticket{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={fetchTickets} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" data-testid="refresh-tickets">
          <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground/50 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40" />
        <input
          data-testid="ticket-search-input"
          type="text"
          placeholder="Search tickets..."
          value={search}
          onChange={e => { setSearch(e.target.value); setSkip(0); }}
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {[
          { value: '', label: 'All' },
          { value: 'ban_appeal', label: 'Appeals' },
          { value: 'general', label: 'General' },
          { value: 'bug', label: 'Bug' },
        ].map(c => (
          <button
            key={c.value}
            data-testid={`filter-category-${c.value || 'all'}`}
            onClick={() => { setCategory(c.value); setSkip(0); }}
            className={`text-[10px] px-2.5 py-1 rounded-full transition-all ${
              category === c.value
                ? 'bg-primary/15 text-primary border border-primary/30'
                : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/10'
            }`}
          >
            {c.label}
          </button>
        ))}
        <div className="w-px bg-white/10 mx-1" />
        {[
          { value: '', label: 'Any' },
          { value: 'open', label: 'Open' },
          { value: 'responded', label: 'Responded' },
          { value: 'closed', label: 'Closed' },
        ].map(s => (
          <button
            key={s.value}
            data-testid={`filter-status-${s.value || 'any'}`}
            onClick={() => { setStatus(s.value); setSkip(0); }}
            className={`text-[10px] px-2.5 py-1 rounded-full whitespace-nowrap transition-all ${
              status === s.value
                ? 'bg-secondary/15 text-secondary border border-secondary/30'
                : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/10'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      {loading && tickets.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => {
            const st = STATUS_STYLES[t.status] || STATUS_STYLES.open;
            const isBanAppeal = t.category === 'ban_appeal';
            const lastMsg = (t.messages || []).slice(-1)[0];
            const unread = lastMsg?.from === 'user' && t.status !== 'closed';

            return (
              <motion.button
                key={t.ticket_id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => openTicket(t.ticket_id)}
                className={`w-full text-left p-3 rounded-xl border transition-colors ${
                  unread
                    ? 'bg-primary/[0.04] border-primary/15 hover:border-primary/25'
                    : 'bg-white/[0.02] border-white/5 hover:border-white/10'
                }`}
                data-testid={`admin-ticket-${t.ticket_id}`}
              >
                <div className="flex items-start gap-3">
                  {/* User avatar */}
                  <div className="w-7 h-7 rounded-full overflow-hidden border border-white/10 flex-shrink-0 mt-0.5">
                    {t.user_picture ? (
                      <img src={t.user_picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-white/5 flex items-center justify-center text-[9px] font-bold text-muted-foreground">
                        {(t.user_name || '?')[0].toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <p className="text-xs font-medium truncate">{t.subject}</p>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${st.bg} ${st.text}`}>{st.label}</span>
                      {isBanAppeal && (
                        <span className="text-[8px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">APPEAL</span>
                      )}
                      {unread && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 truncate">{t.user_name} - {t.user_email}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] text-muted-foreground/40 font-mono">{t.ticket_id}</span>
                      <span className="text-[9px] text-muted-foreground/40">{timeAgo(t.updated_at)}</span>
                      <span className="text-[9px] text-muted-foreground/40">{(t.messages || []).length} msg</span>
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
          {tickets.length === 0 && (
            <div className="text-center py-12">
              <Ticket className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground/40">No tickets found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
          <button
            data-testid="tickets-prev-page"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
            className="text-[10px] px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors disabled:opacity-30 inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <span className="text-[10px] text-muted-foreground/50">
            Page {currentPage} of {totalPages}
          </span>
          <button
            data-testid="tickets-next-page"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
            className="text-[10px] px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors disabled:opacity-30 inline-flex items-center gap-1"
          >
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
